import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Briefcase,
  ChevronRight,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  Save,
  Tag,
  Trash2,
  XCircle,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import {
  CompanyForm,
  fromCompany,
  type CompanyFormValues,
} from "@/components/clients/CompanyForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBanner } from "@/components/ui/status-banner";
import { useActiveClientId } from "@/hooks/useActiveClient";
import { supabase } from "@/lib/supabase";
import {
  EXPERIENCE_LABELS,
  type Company,
  type Search as JobSearch,
} from "@/lib/types";

export const Route = createFileRoute("/clients_/$clientId")({
  component: ClientDetailPage,
});

function ClientDetailPage() {
  const { clientId } = Route.useParams();
  return (
    <LoginGate title="DeepDive" subtitle="Iniciá sesión para ver el cliente.">
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <ClientDetail clientId={clientId} />
        </AppShell>
      )}
    </LoginGate>
  );
}

function ClientDetail({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [, setActiveClientId] = useActiveClientId();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Sincronizar cliente activo con la URL: si entrás a /clients/$id directo,
  // que el filtro global del header arranque sobre ese cliente.
  useEffect(() => {
    setActiveClientId(clientId);
  }, [clientId, setActiveClientId]);
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    title: string;
    detail?: string;
  } | null>(null);

  const { data: company, isLoading } = useQuery({
    queryKey: ["company", clientId],
    queryFn: async (): Promise<Company> => {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("id", clientId)
        .single();
      if (error) throw error;
      return data as Company;
    },
  });

  const { data: searches = [] } = useQuery({
    queryKey: ["searches", "by-company", clientId],
    queryFn: async (): Promise<JobSearch[]> => {
      const { data, error } = await supabase
        .from("searches")
        .select("*")
        .eq("company_id", clientId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as JobSearch[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (form: CompanyFormValues) => {
      if (!form.name.trim()) throw new Error("El nombre es requerido.");
      const { error } = await supabase
        .from("companies")
        .update({
          name: form.name.trim(),
          razon_social: form.razon_social.trim() || null,
          email: form.email.trim().toLowerCase() || null,
          phone: form.phone.trim() || null,
          rubro: form.rubro.trim() || null,
          is_active: form.is_active,
          notes: form.notes.trim() || null,
        })
        .eq("id", clientId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["company", clientId] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      setFeedback({ kind: "success", title: "Cambios guardados" });
      setEditOpen(false);
    },
    onError: (e: Error) =>
      setFeedback({ kind: "error", title: "Error guardando", detail: e.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("companies")
        .delete()
        .eq("id", clientId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      navigate({ to: "/clients" });
    },
    onError: (e: Error) =>
      setFeedback({
        kind: "error",
        title: "No se pudo borrar",
        detail:
          e.message +
          (e.message.includes("foreign key")
            ? " — el cliente tiene búsquedas o invitaciones asociadas."
            : ""),
      }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="px-6 py-5">
        <StatusBanner variant="error" title="Cliente no encontrado" />
        <div className="mt-4">
          <Button variant="default" asChild>
            <Link to="/clients">
              <ArrowLeft className="h-3.5 w-3.5" />
              Volver
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const openSearches = searches.filter((s) => s.is_open).length;

  return (
    <div className="px-6 py-5">
      <Link
        to="/clients"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-3 w-3" />
        Clientes
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-12 w-12 rounded-md bg-secondary border border-border flex items-center justify-center text-sm font-bold shrink-0">
            {company.name
              .split(/\s+/)
              .slice(0, 2)
              .map((p) => p[0]?.toUpperCase() ?? "")
              .join("")}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight truncate">
                {company.name}
              </h1>
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                title="Editar cliente"
                aria-label="Editar cliente"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              {!company.is_active && (
                <Badge variant="outline">Inactiva</Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              {searches.length}{" "}
              {searches.length === 1 ? "búsqueda" : "búsquedas"}
              {openSearches > 0 && ` · ${openSearches} abierta${openSearches === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>
        <Button
          variant="outlineDanger"
          size="sm"
          onClick={() => setDeleteOpen(true)}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Borrar cliente
        </Button>
      </div>

      {feedback && (
        <div className="mb-6">
          <StatusBanner
            variant={feedback.kind}
            title={feedback.title}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.detail}
          </StatusBanner>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
        {/* === Info card compacto === */}
        <Card className="p-5 lg:col-span-2">
          <CompanyInfoCard
            company={company}
            onEdit={() => setEditOpen(true)}
          />
        </Card>

        {/* === Tabla de búsquedas === */}
        <Card className="overflow-hidden lg:col-span-3">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold tracking-tight">
                Búsquedas ({searches.length})
              </h2>
            </div>
            <Button variant="primary" size="sm" asChild>
              <Link to="/searches/new" search={{ company_id: clientId }}>
                <Plus className="h-3.5 w-3.5" />
                Nueva
              </Link>
            </Button>
          </div>

          {searches.length === 0 ? (
            <div className="py-12 text-center px-6">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 border border-primary/30 mb-3">
                <Briefcase className="h-4 w-4 text-primary" />
              </div>
              <h3 className="text-sm font-medium mb-1">
                Sin búsquedas todavía
              </h3>
              <p className="text-xs text-muted-foreground mb-4">
                Agregá la primera búsqueda para invitar candidatos.
              </p>
              <Button variant="primary" size="sm" asChild>
                <Link to="/searches/new" search={{ company_id: clientId }}>
                  <Plus className="h-3.5 w-3.5" />
                  Nueva búsqueda
                </Link>
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-border max-h-[640px] overflow-y-auto">
              {searches.map((s) => (
                <SearchRow key={s.id} search={s} />
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* === Edit dialog === */}
      <EditClientDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        company={company}
        saving={saveMutation.isPending}
        onSave={(form) => saveMutation.mutate(form)}
      />

      {/* === Confirm delete === */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-4 w-4" />
              Borrar cliente
            </DialogTitle>
            <DialogDescription>
              Se va a borrar <strong>{company.name}</strong>. Si tiene búsquedas
              asociadas también se borran (cascade). Las invitaciones existentes
              quedan en la base pero pierden el vínculo con el cliente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="default" onClick={() => setDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              loading={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Borrar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =================== COMPANY INFO CARD ===================
function CompanyInfoCard({
  company,
  onEdit,
}: {
  company: Company;
  onEdit: () => void;
}) {
  const fields: { icon: typeof Mail; label: string; value: string | null }[] = [
    { icon: Mail, label: "Email", value: company.email },
    { icon: Phone, label: "Teléfono", value: company.phone },
    { icon: Tag, label: "Rubro", value: company.rubro },
  ];
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold tracking-tight">Información</h2>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title="Editar"
          aria-label="Editar"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>

      <dl className="space-y-3">
        {company.razon_social && (
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              Razón Social
            </dt>
            <dd className="text-sm">{company.razon_social}</dd>
          </div>
        )}
        {fields.map(({ icon: Icon, label, value }) =>
          value ? (
            <div key={label} className="flex items-start gap-2.5">
              <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
              <div className="min-w-0">
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                  {label}
                </dt>
                <dd className="text-sm break-all">{value}</dd>
              </div>
            </div>
          ) : null
        )}
        {company.notes && (
          <div className="pt-3 border-t border-border">
            <dt className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Notas internas
            </dt>
            <dd className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {company.notes}
            </dd>
          </div>
        )}
      </dl>
    </>
  );
}

// =================== EDIT DIALOG ===================
function EditClientDialog({
  open,
  onOpenChange,
  company,
  saving,
  onSave,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  company: Company;
  saving: boolean;
  onSave: (form: CompanyFormValues) => void;
}) {
  const [form, setForm] = useState<CompanyFormValues>(() => fromCompany(company));

  function handleOpenChange(next: boolean) {
    if (next) {
      // Refresh form from latest company when opening
      setForm(fromCompany(company));
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar cliente</DialogTitle>
          <DialogDescription>
            Modificá los datos y guardá los cambios.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <CompanyForm value={form} onChange={setForm} />
        </div>
        <DialogFooter>
          <Button variant="default" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => onSave(form)}
            loading={saving}
            disabled={!form.name.trim()}
          >
            <Save className="h-4 w-4" />
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================== SEARCH ROW ===================
function SearchRow({ search }: { search: JobSearch }) {
  return (
    <li className="group hover:bg-accent/40 transition-colors">
      <Link
        to="/searches/$searchId"
        params={{ searchId: search.id }}
        className="px-5 py-3 flex items-center gap-3"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{search.puesto}</span>
            <Badge variant="muted" className="text-[9px] normal-case tracking-normal">
              {EXPERIENCE_LABELS[search.experience]}
            </Badge>
            {!search.is_open && (
              <Badge variant="outline" className="text-[9px]">
                Cerrada
              </Badge>
            )}
          </div>
          {search.technologies.length > 0 && (
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              {search.technologies.slice(0, 5).map((t) => (
                <span
                  key={t}
                  className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded font-mono"
                >
                  {t}
                </span>
              ))}
              {search.technologies.length > 5 && (
                <span className="text-[10px] text-muted-foreground">
                  +{search.technologies.length - 5}
                </span>
              )}
            </div>
          )}
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-50 group-hover:opacity-100 transition-opacity" />
      </Link>
    </li>
  );
}
